import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CheckCircle2, ChevronRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import type { ReconOverviewSearch } from "..";
import {
  defaultClosePeriod,
  monthLabel,
  recentMonths,
  useOpenPeriod,
  useReconPeriod,
  useReopen,
  useSignOff,
  type PeriodDetail,
  type StepState,
} from "../api";
import { StepAssignmentsTab } from "../components/StepAssignmentsTab";
import { RECON_ROLE_LABEL, modeLabel, stepDisplayNo, stepStatusChip } from "../steps";

export function ReconciliationOverviewPage() {
  const { period: routePeriod } = useParams({ strict: false }) as { period?: string };
  const period = routePeriod ?? defaultClosePeriod();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReconOverviewSearch;
  const canAdmin = useCan("reconciliation.write");
  const tab = search.tab === "admin" && canAdmin ? "admin" : "steps";
  const { data, error, isLoading } = useReconPeriod(period);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Monthly Reconciliation</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            The month-end close that keeps the contract world honest against billed
            revenue — owner-routed steps, signed off before the 7th. Pick a month to
            review its close; open a step for its detail, data, and your input.
          </p>
        </div>
        <MonthPicker
          period={period}
          onChange={(p) =>
            navigate({
              to: "/reconciliation/$period",
              params: { period: p },
              search: (prev: ReconOverviewSearch) => prev,
            })
          }
        />
      </div>

      {canAdmin && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(["steps", "admin"] as const).map((t) => (
            <button
              key={t}
              onClick={() =>
                navigate({
                  to: "/reconciliation/$period",
                  params: { period },
                  search: t === "admin" ? { tab: "admin" } : {},
                })
              }
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
                tab === t
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "admin" ? "Admin" : "Reconcile Steps"}
            </button>
          ))}
        </div>
      )}

      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load this month's reconciliation</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {tab === "admin" ? (
        <StepAssignmentsTab />
      ) : (
        <>
          <CompletionBanner period={period} data={data ?? null} loading={isLoading} />
          <StepListCard period={period} data={data ?? null} loading={isLoading} />
        </>
      )}
    </div>
  );
}

function MonthPicker({
  period,
  onChange,
}: {
  period: string;
  onChange: (p: string) => void;
}) {
  const months = recentMonths(18);
  const options = months.includes(period) ? months : [period, ...months];
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
        Close month
      </span>
      <Select value={period} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-[150px]">
          <SelectValue>{monthLabel(period)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((m) => (
            <SelectItem key={m} value={m}>
              {monthLabel(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CompletionBanner({
  period,
  data,
  loading,
}: {
  period: string;
  data: PeriodDetail | null;
  loading: boolean;
}) {
  const open = useOpenPeriod(period);
  const signOff = useSignOff(period);
  const reopen = useReopen(period);

  if (loading && !data) return <Card className="h-[92px] animate-pulse" />;

  const status = data?.status ?? "not_started";
  const done = data?.steps_done ?? 0;
  const total = data?.steps_total ?? 0;
  const signedOff = status === "signed_off";

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
      <div className="flex items-center gap-4">
        <div
          className={cn(
            "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full",
            signedOff
              ? "bg-success-light text-success"
              : status === "not_started"
                ? "bg-neutral-light text-muted-foreground"
                : "bg-warning-light text-warning",
          )}
        >
          {signedOff ? (
            <CheckCircle2 className="h-6 w-6" />
          ) : (
            <Loader2 className="h-6 w-6" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{monthLabel(period)} close</span>
            <StatusBadge value={status} />
          </div>
          <p className="text-sm text-muted-foreground">
            {signedOff
              ? `Signed off by ${data?.signed_off_by_name ?? "—"}${
                  data?.signed_off_at
                    ? ` on ${new Date(data.signed_off_at).toLocaleDateString()}`
                    : ""
                }.`
              : status === "not_started"
                ? "Not opened yet. Open the close to seed the step worklist."
                : `${done} of ${total} steps complete.`}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!data?.exists && data?.can_open && (
          <Button
            onClick={() =>
              open.mutate(undefined, {
                onSuccess: () => toast.success(`${monthLabel(period)} close opened`),
                onError: (e) =>
                  toast.error("Couldn't open close", { description: (e as Error).message }),
              })
            }
            disabled={open.isPending}
          >
            {open.isPending ? "Opening…" : "Open close"}
          </Button>
        )}
        {status === "in_progress" && data?.can_sign_off && (
          <Button
            onClick={() =>
              signOff.mutate(undefined, {
                onSuccess: () => toast.success("Close signed off"),
                onError: (e) =>
                  toast.error("Couldn't sign off", { description: (e as Error).message }),
              })
            }
            disabled={signOff.isPending}
          >
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            {signOff.isPending ? "Signing off…" : "Sign off"}
          </Button>
        )}
        {signedOff && data?.can_sign_off && (
          <Button
            variant="outline"
            onClick={() =>
              reopen.mutate(undefined, {
                onSuccess: () => toast.success("Close reopened"),
                onError: (e) =>
                  toast.error("Couldn't reopen", { description: (e as Error).message }),
              })
            }
            disabled={reopen.isPending}
          >
            {reopen.isPending ? "Reopening…" : "Reopen"}
          </Button>
        )}
      </div>
    </Card>
  );
}

function StepListCard({
  period,
  data,
  loading,
}: {
  period: string;
  data: PeriodDetail | null;
  loading: boolean;
}) {
  if (loading && !data) return <Card className="h-64 animate-pulse" />;
  const steps = data?.steps ?? [];
  return (
    <Card className="divide-y divide-border overflow-hidden p-0">
      {steps.map((step) => (
        <StepRow key={step.step_key} period={period} step={step} />
      ))}
    </Card>
  );
}

function StepRow({ period, step }: { period: string; step: StepState }) {
  return (
    <Link
      to="/reconciliation/$period/$stepKey"
      params={{ period, stepKey: step.step_key }}
      className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/50"
    >
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
        {stepDisplayNo(step.order)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{step.title}</span>
          {!step.can_act && (
            <Lock
              className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground"
              aria-label="You can't act on this step"
            />
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>{modeLabel(step.mode)}</span>
          <span>·</span>
          <span>
            {step.owner_roles
              .map((r) => RECON_ROLE_LABEL[r]?.short ?? r)
              .join(", ")}
          </span>
          {step.status === "complete" && step.completed_at && (
            <>
              <span>·</span>
              <span className="text-success">
                Signed off by {step.completed_by_name ?? "—"} ·{" "}
                {new Date(step.completed_at).toLocaleString()}
              </span>
            </>
          )}
        </div>
      </div>
      <StatusBadge {...stepStatusChip(step.status)} />
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
    </Link>
  );
}

